import React, { useState, useMemo, useEffect } from "react";
import styled from "styled-components";
import { TabbedCard } from '../components/Ui/Card.jsx';
import Table, { SelectColumnFilter } from "../components/Ui/Table.jsx";
import { TabController } from '../components/TabController/TabController.jsx';
import { TabPanel } from '../components/TabController/TabPanel.jsx';
import { Alert } from "../components/Ui/Alert.jsx";
import SelectWithLabel from "../components/Inputs/Select.jsx";
import { formatDateToDDMMYYYY } from "../components/Helpers/dateFns.js";
import {
  fetchFrames,
  fetchUserFrames,
  deleteUserFrames,
  addUserFrames
} from '../http/frames.js';
import {
  fetchOutstandingSamples
} from '../http/samples.js';
import {
  fetchLensTintsCases,
  fetchUserLensTintsCases,
  deleteUserLensTintsCases,
  addUserLensTintsCases
} from '../http/extras.js';
import {
  fetchAccount,
  updateAccount,
} from "../http/account.js";
import { DefaultButton, DefaultButtonSmall } from "../components/StyledComponents/Buttons/Buttons.jsx";
import { CenterContainer, FormContainer, Label } from "../components/StyledComponents/Forms/Forms.jsx";
import { TabContent } from "../components/StyledComponents/Tabs/Tabs.jsx";
import { opticians } from "../components/Helpers/userTypes.js";
import FittingFramesModal from "../components/Ui/Opticians/FittingFramesModal.jsx";

const HoveredImageContainer = styled.div`
  position: fixed;
  top: 50%;
  right: 30%;
  transform: translateY(-50%);
  z-index: 1000;
  pointer-events: none;
`;

const HoveredImage = styled.img`
  max-width: 500px;
  max-height: 500px;
`;

export default function Configuration({ colour, email, accountType }) {

  const [hoveredImage, setHoveredImage] = useState(null);
  const [account, setAccount] = useState({});
  const [originalAccount, setOriginalAccount] = useState({});
  const [successCreateSample, setSuccessCreateSample] = useState(false);

  const [selectedFrameType, setSelectedFrameType] = useState();

  const [frames, setFrames] = useState([]);
  const [userFrames, setUserFrames] = useState([]);

  const [samples, setSamples] = useState([]);

  const [extras, setExtras] = useState([]);
  const [userExtras, setUserExtras] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [warningValidation, setWarningValidation] = useState({ error: false, message: "" });
  const [fittingFramesModal, setFittingFramesModal] = React.useState(false);

  useEffect(() => {
    const loadFrames = async () => {
      setIsLoading(true);
      try {
        const frames = await fetchFrames();
        setFrames(frames);
        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    const loadOutstandingSamples = async () => {
      setIsLoading(true);
      try {
        const samples = await fetchOutstandingSamples();
        setSamples(samples);
        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    const loadExtras = async () => {
      setIsLoading(true);
      try {
        const extras = await fetchLensTintsCases();
        setExtras(extras);
        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    const loadAccount = async () => {
      setIsLoading(true);
      try {
        const fetchedAccount = await fetchAccount(email);

        setOriginalAccount(fetchedAccount);
        setAccount(fetchedAccount.Users_Profile);
        setError(null);
        setSelectedFrameType(fetchedAccount?.Users_Profile?.safetyorvdu);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    loadAccount();
    loadFrames();
    loadExtras();
    loadUserExtras();
    loadUserFrames();
    loadOutstandingSamples();

  }, [email]);

  const loadUserExtras = async () => {
    setIsLoading(true);
    try {
      const extras = await fetchUserLensTintsCases(email);
      setUserExtras(extras);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserFrames = async () => {
    setIsLoading(true);
    try {
      const frames = await fetchUserFrames();
      setUserFrames(frames);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveChanges = async () => {
    setIsLoading(true);
    try {
      originalAccount.Users_Profile = account;
      await updateAccount(originalAccount);

      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
      setSuccess(true);
    }
  };

  const frameTypes = [
    { label: 'Safety & DSE', value: 'safevdu' },
    { label: 'Safety Only', value: 'safe' },
    { label: 'DSE Only', value: 'vdu' }
  ];

  const lensMaterials = [
    { label: 'CR39 and Polycarbonate', value: 'crandpoly' },
    { label: 'CR39', value: 'cr39' },
    { label: 'Polycarbonate', value: 'poly' }
  ];

  const lensTypes = [
    { label: 'Single Vision, Bifocal, Varifocal', value: 'svbfvf' },
    { label: 'Single Vision, Bifocal', value: 'svbf' },
    { label: 'Single Vision Only', value: 'sv' }
  ];

  const filteredData = useMemo(() => {
    if (selectedFrameType === 'vdu') {
      return frames.filter(item => item.frametype === 'DSE');
    } else if (selectedFrameType === 'safe') {
      return frames.filter(item => item.frametype !== 'DSE');
    } else {
      return frames;
    }
  }, [selectedFrameType, frames]);

  const handleOpticianChange = (field, value) => {
    setOriginalAccount(prev => ({
      ...prev,
      Optician_Profile: {
        ...prev.Optician_Profile,
        [field]: value,
      }
    }));
  };

  const handleChange = (field, value) => {
    setAccount(prev => ({ ...prev, [field]: value }));
  };

  const handleExtrasChange = async (partCode, checked) => {
    try {
      if (checked) {
        const extra = extras.find(x => x.partcode === partCode);

        await addUserLensTintsCases(email, extra.description, extra.partcode);
        await loadUserExtras();
      } else {
        const userExtra = userExtras.find(x => x.addonselectpartcode === partCode);

        await deleteUserLensTintsCases(userExtra.unqidaddon);
        setUserExtras(prev => prev.filter(extra => extra.addonselectpartcode !== partCode));
      }
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFramesChange = async (partCode, checked) => {
    try {
      if (checked) {
        const frame = frames.find(x => x.partcode === partCode);

        await addUserFrames(email, frame.partcode, frame.frameName);
        await loadUserFrames();
      } else {
        const userFrame = userFrames.find(x => parseInt(x.frameselecpartcode) === partCode);

        await deleteUserFrames(userFrame.uniidframe);
        setUserFrames(prev => prev.filter(extra => parseInt(extra.frameselecpartcode) !== partCode));
      }
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  };

  const isPartCodeSelected = (partCode) => {
    return userExtras.some(userExtra => parseInt(userExtra.addonselectpartcode) === partCode);
  };

  const isFramePartCodeSelected = (partCode) => {
    return userFrames.some(userFrame => parseInt(userFrame.frameselecpartcode) === partCode);
  };

  const validateCaseSelection = (partCode) => {
    const extra = extras.filter(extra => extra.partcode === partCode);

    if (extra.length > 0 && extra[0].Category === "Case") {
      const cases = extras.filter(e => e.Category === extra[0].Category);

      const casePartcodes = new Set(cases.map(c => c.partcode));

      const matchingPartcodesCount = userExtras.filter(userExtra => casePartcodes.has(userExtra.addonselectpartcode)).length;

      return matchingPartcodesCount === 1;
    }
    return false;
  };

  const extrasColumns = React.useMemo(() => [
    {
      id: 'selected',
      accessor: "partcode",
      Cell: ({ value }) => (
        <input
          type="checkbox"
          checked={isPartCodeSelected(value)}
          onChange={(e) => {

            let caseValidationDuplicates = false;
            if (!e.target.checked) {
              caseValidationDuplicates = validateCaseSelection(value);
            }

            if (!caseValidationDuplicates) {
              setWarningValidation({ error: false, message: "" });
              handleExtrasChange(value, e.target.checked);
            } else {
              setWarningValidation({ error: true, message: "You must have at least one case type selected." });
            }
          }}
        />
      )
    },
    {
      Header: "Description",
      accessor: "description",
    },
    {
      Header: "Category",
      accessor: "Category",
      Filter: SelectColumnFilter,
      filter: 'includes',
    }
  ], [userExtras, extras]);

  const outstandingSampleColumns = React.useMemo(() => [
    {
      Header: "Frame Details",
      accessor: "FrameDetails",
    },
    {
      Header: "Patient Reference",
      accessor: "PatientReference",
    },
    {
      Header: "Order Date",
      accessor: row => formatDateToDDMMYYYY(row.OrderDate),
    },
    {
      Header: "Return By Date",
      accessor: row => formatDateToDDMMYYYY(row.ReturnByDate),
    },
    {
      Header: "Days Out",
      accessor: "DaysOut",
    },

  ], [samples]);

  const framesColumns = React.useMemo(() => [
    {
      id: 'selected',
      accessor: 'partcode',
      Cell: ({ value }) => (
        <input
          type="checkbox"
          checked={isFramePartCodeSelected(value)}
          onChange={(e) => handleFramesChange(value, e.target.checked)}
        />
      ),
      Header: () => <div style={{ width: '30px' }}> </div>,
      width: 30,
    },
    {
      accessor: "ImageByte",
      Cell: ({ row }) => {
        const imageByte = row.original.ImageByte;
        const urlDirect = row.original.ImageUrl;

        return (
          <img
            src={`data:image/jpeg;base64,${imageByte}`}
            alt="Frame"
            style={{ maxWidth: '50px', maxHeight: '50px', objectFit: 'contain', cursor: 'pointer' }}
            onMouseEnter={() => setHoveredImage(imageByte)}
            onMouseLeave={() => setHoveredImage(null)}
            onClick={() => window.open(urlDirect)}
          />);
      },
      Header: () => <div style={{ width: '50px' }}> </div>,
      width: 20,
    },
    {
      Header: "Name",
      accessor: "frameName",
    },
    {
      Header: "Material",
      accessor: "frameMaterial",
    },
    {
      Header: "Type",
      accessor: "frametype",
      Filter: SelectColumnFilter,
      filter: 'includes',
    }
  ], [userFrames, frames]);

  const message = successCreateSample
  ? (
    <>
      Successfully Created Fitting Frame Request for patient: <strong>{successCreateSample}</strong>. 
    </>
  )
  : "Below is a list of outstanding fitting frames you have requested. Please note that a maximum of 4 frames is permitted per optical branch at any one time. Please ensure all frames are returned promptly once they have been used.";

  return (
    <>
    {fittingFramesModal &&
            <FittingFramesModal
              showHideModal={setFittingFramesModal}
              isModalOpen={!!fittingFramesModal}
              colour={colour}
              opticiansId={account.unqID}
              fnSuccess={setSuccessCreateSample}
              fnError={setError}
            />
          }
    <CenterContainer>
      <FormContainer>
        <TabbedCard
          label={opticians.includes(accountType) ? "Fitting Frames" : 'EyeTicket™ Configuration'}
          headercolour={colour.sidebar}
          headerTextColour={colour.font}
          tabController={
            <TabController colours={colour}>

              <TabPanel
                label={opticians.includes(accountType) ? "Fitting Frames Held in Pratice" : "Frames"}
                visible={true}
                disabled={account.lock_eyewear_profile === 1}
                disabledMessage={account.lock_eyewear_profile === 1 && "EyeTicket modification has been disabled for this account."}>
                <TabContent>
                  <SelectWithLabel
                    disabled={opticians.includes(accountType)}
                    label="Frame Types"
                    options={frameTypes}
                    value={account.safetyorvdu}
                    onChange={(event) => {
                      setSelectedFrameType(event.target.value);
                      handleChange('safetyorvdu', event.target.value);
                    }} />
                  <Table
                    columns={framesColumns}
                    data={filteredData}
                    info={opticians.includes(accountType) ? "Please tick the boxes of all fitting frames you hold within your practice" : "Please tick the box to add a frame to your profile"}
                    type={"info"}
                    overridePageLen={true}
                    defaultPageSize={20} />
                  {hoveredImage && (
                    <HoveredImageContainer>
                      <HoveredImage
                        src={`data:image/jpeg;base64,${hoveredImage}`}
                        alt="Enlarged Frame"
                      />
                    </HoveredImageContainer>
                  )}
                  <Alert type="info">Please note there is an upgrade charge for the metal IceJem Safety frames</Alert>
                </TabContent>
              </TabPanel>
              <TabPanel
                label="Fitting Frame Request"
                visible={opticians.includes(accountType)}>
                <TabContent>
                  <Table
                    columns={outstandingSampleColumns}
                    data={samples}
                    info={message}
                    type={successCreateSample ? 'success' : 'info'}
                    overridePageLen={true}
                    defaultPageSize={20} 
                    children={
                      <div style={{ textAlign: 'right', marginRight: '10px'}}>
                    <DefaultButtonSmall onClick={() => setFittingFramesModal(true)}>New Fitting Frame Request</DefaultButtonSmall>
                    </div>
                    }/>
                </TabContent>
              </TabPanel>
              <TabPanel
                label="Lenses & Cases"
                visible={!opticians.includes(accountType)}
                disabled={account.lock_eyewear_profile === 1}
                disabledMessage={account.lock_eyewear_profile === 1 && "EyeTicket modification has been disabled for this account."}>
                <TabContent>
                  <SelectWithLabel label="Permitted Lens Material" options={lensMaterials} value={account.lensmaterial} onChange={(event) => handleChange('lensmaterial', event.target.value)} />
                  <SelectWithLabel label="Permitted Lens Types" options={lensTypes} value={account.lenstype} onChange={(event) => handleChange('lenstype', event.target.value)} />
                  {warningValidation.error && <Alert type='warning'>{warningValidation.message}</Alert>}
                  <Label>Coats, Tints & Cases</Label>
                  <Table columns={extrasColumns} data={extras} showSearch={false} />
                </TabContent>
              </TabPanel>
            </TabController>
          }
        >
          {isLoading && <p>Loading...</p>}
          {error && !isLoading && <Alert type="error"><b>{error.message}</b></Alert>}
          {success && <Alert type='success'>Account updated successfully</Alert>}
          {!opticians.includes(accountType) && <DefaultButton onClick={handleSaveChanges} disabled={account.lock_eyewear_profile === 1 || isLoading}>Save Changes</DefaultButton>}
        </TabbedCard>
      </FormContainer>
    </CenterContainer>
    </>
  );
}